import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  TApiApplicationsResponse,
  TApiApplicationDetails,
  TApiApplicationCreated,
  TApiApplicationDocument,
  TApplicationDocumentBodyResponse,
  TApplicationDocumentHistoryResponse,
  TApiApplicationDocumentFilePayload,
  TApiApplicationDocumentPayload,
  TApiApplicationDocumentsResponse,
  TApiApplicationUnsignedDocumentsResponse,
  TApiSubmitApplicationResponse,
  TApiUploadApplicationDocumentResponse,
  TApplicationStatus,
  TCreateApplicationRequest,
} from '~/modules/covenants/types.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import api from '~/libs/api';
import { RootState } from '~/store/store.ts';
import { getRequestErrorStatus } from '~/store/covenants/thunks/utils.ts';
import {
  mergeApplicationDocumentFilePayload,
  normalizeApplicationDocuments,
} from '~/modules/covenants/utils.ts';

export const fetchApplications = createAsyncThunk<
  TApiApplicationsResponse,
  {
    contragentId: number;
    page: number;
    size: number;
    status?: TApplicationStatus;
  },
  { state: RootState }
>(
  'covenantsPage/fetchApplications',
  async ({ contragentId, page, size, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/contragents/${contragentId}/applications`,
        {
          params: {
            page,
            size,
            ...(status ? { status } : {}),
          },
        }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchActiveApplications = createAsyncThunk<
  TApiApplicationsResponse,
  { contragentId: number; page: number; size: number },
  { state: RootState }
>(
  'covenantsPage/fetchActiveApplications',
  async ({ contragentId, page, size }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/contragents/${contragentId}/applications`,
        {
          params: {
            status: 'NEW',
            page,
            size,
          },
        }
      );
      return {
        applications: data.applications ?? [],
        count: data.count ?? data.applications?.length ?? 0,
        pageConfiguration: data.pageConfiguration ?? {
          page,
          size,
          totalElements: data.count ?? data.applications?.length ?? 0,
        },
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchApplication = createAsyncThunk<
  TApiApplicationDetails,
  { contragentId: number; applicationId: string },
  { state: RootState }
>(
  'covenantsPage/fetchApplication',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}`
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchApplicationDocuments = createAsyncThunk<
  TApiApplicationDocumentsResponse,
  {
    contragentId: number;
    applicationId: string;
    page?: number;
    size?: number;
    status?: TApplicationStatus;
  },
  { state: RootState }
>(
  'covenantsPage/fetchApplicationDocuments',
  async ({ contragentId, applicationId, page, size, status }, { rejectWithValue }) => {
    try {
      return await requestApplicationDocumentsPage({
        contragentId,
        applicationId,
        page,
        size,
        status,
      });
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const requestApplicationDocumentsPage = async ({
  contragentId,
  applicationId,
  page,
  size,
  status,
}: {
  contragentId: number;
  applicationId: string;
  page?: number;
  size?: number;
  status?: TApplicationStatus;
}): Promise<TApiApplicationDocumentsResponse> => {
  const { data } = await api.get(
    `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents`,
    {
      params: {
        ...(page !== undefined ? { page } : {}),
        ...(size !== undefined ? { size } : {}),
        ...(status === 'NEW' ? { 'with-required': true } : {}),
      },
    }
  );

  const applicationDocuments = (data.applicationDocuments ?? []).flatMap(
    (document: TApiApplicationDocumentPayload) => normalizeApplicationDocuments(document)
  );
  const totalElements =
    data.pageConfiguration?.totalElements ?? data.count ?? applicationDocuments.length;

  return {
    applicationDocuments,
    count: totalElements,
    pageConfiguration: data.pageConfiguration ?? {
      page: page ?? 0,
      size: size ?? applicationDocuments.length,
      totalElements,
    },
  };
};

export const fetchApplicationUnsignedDocumentsCount = createAsyncThunk<
  number | null,
  {
    contragentId: number;
    applicationId: string;
  },
  { state: RootState }
>(
  'covenantsPage/fetchApplicationUnsignedDocumentsCount',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TApiApplicationUnsignedDocumentsResponse>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/unsinged`
      );
      const unsignedDocumentsCount = Number(data?.unsinged);

      return Number.isFinite(unsignedDocumentsCount) ? unsignedDocumentsCount : null;
    } catch (error) {
      if (getRequestErrorStatus(error) === 404) {
        return null;
      }

      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchApplicationDocument = createAsyncThunk<
  TApiApplicationDocument,
  {
    contragentId: number;
    applicationId: string;
    id: string;
    baseDocument: TApiApplicationDocument;
  },
  { state: RootState }
>(
  'covenantsPage/fetchApplicationDocument',
  async ({ contragentId, applicationId, id, baseDocument }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/${id}`
      );

      return mergeApplicationDocumentFilePayload(
        baseDocument,
        data as TApiApplicationDocumentFilePayload
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchApplicationDocumentHistory = createAsyncThunk<
  TApplicationDocumentHistoryResponse,
  { applicationId: string; documentId: string; page: number; size: number },
  { state: RootState }
>(
  'covenantsPage/fetchApplicationDocumentHistory',
  async ({ applicationId, documentId, page, size }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/applications/${applicationId}/application-documents/${documentId}`,
        { params: { page, size } }
      );

      return {
        content: data.content ?? [],
        number: data.number ?? page,
        size: data.size ?? size,
        totalElements: data.totalElements ?? data.content?.length ?? 0,
        totalPages: data.totalPages ?? 0,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchApplicationDocumentBody = createAsyncThunk<
  TApplicationDocumentBodyResponse,
  { id: number },
  { state: RootState }
>('covenantsPage/fetchApplicationDocumentBody', async ({ id }, { rejectWithValue }) => {
  try {
    const { data } = await api.get(`/covenants-api/covenants/application-documents/${id}`);
    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const createApplication = createAsyncThunk<
  TApiApplicationCreated,
  { contragentId: number; payload?: TCreateApplicationRequest },
  { state: RootState }
>('covenantsPage/createApplication', async ({ contragentId, payload }, { rejectWithValue }) => {
  try {
    const { data } = await api.post(
      `/covenants-api/covenants/contragents/${contragentId}/applications`,
      payload ?? null
    );
    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const recreateApplication = createAsyncThunk<
  TApiApplicationCreated,
  { contragentId: number; applicationId: string }
>(
  'covenantsPage/recreateApplication',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/recreate`,
        null
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitApplication = createAsyncThunk<
  TApiSubmitApplicationResponse,
  { contragentId: number; applicationId: string; version: number },
  { state: RootState }
>(
  'covenantsPage/submitApplication',
  async ({ contragentId, applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/submit`,
        null,
        {
          params: { version },
        }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const uploadApplicationDocument = createAsyncThunk<
  TApiUploadApplicationDocumentResponse,
  {
    contragentId: number;
    applicationId: string;
    documentId: string;
    childPeriod?: string;
    yearPeriod?: string;
    deadline?: string | null;
    document: File | Blob;
    version: number;
  },
  { state: RootState }
>(
  'covenantsPage/uploadApplicationDocument',
  async (
    {
      contragentId,
      applicationId,
      documentId,
      childPeriod,
      yearPeriod,
      deadline,
      document,
      version,
    },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();

      formData.append('document', document);

      const url = `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents`;

      const params: Record<string, string | number> = { documentId, version };
      if (childPeriod) params.childPeriod = childPeriod;
      if (yearPeriod) params.yearPeriod = yearPeriod;
      params.deadline = deadline ?? 'null';

      const { data } = await api.post(url, formData, {
        params,
      });
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const covenantsSaveSignedDocumentsFromDiia = createAsyncThunk<
  void,
  { contragentId: number; applicationId: string; documentIds: string[] },
  { state: RootState }
>(
  'covenants/saveSignedDocumentsFromDiia',
  async ({ contragentId, applicationId, documentIds }, { rejectWithValue }) => {
    try {
      for (const docId of documentIds) {
        const path = `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/${docId}/save-signed-documents`;
        await api.post(path, null);
      }
      return;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
